import type { useAccount } from '../app/hooks/useAccount';
import type { UsagePayload } from '../engines/ai/client';

export interface AccountMenuProps {
  open: boolean;
  onClose: () => void;
  account: ReturnType<typeof useAccount>;
  /** 用量与单价（服务端权威）：展示今日免费次数与新计费规则。 */
  usage?: UsagePayload | null;
  onRefreshUsage?: () => void;
}

/**
 * 账号抽屉：一个账号，YOGO 全系工具通用。
 *
 * 本站不提供密码登录/注册/激活 —— 这些统一由 YoGo 账号中心负责，
 * 因此未登录态只有一个主按钮（整页跳转到账号中心），
 * 已登录态展示身份与来自账号中心的积分余额。
 */
export function AccountMenu({ open, onClose, account, usage }: AccountMenuProps) {
  if (!open) return null;

  const user = account.session?.user ?? null;

  return (
    <div className="overlay" onClick={onClose} role="presentation">
      <aside className="drawer" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="YoGo 账号">
        <div className="drawer-head">
          <span className="card-title">YoGo 账号</span>
          <span className="spacer" />
          <button type="button" className="btn btn-ghost" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </div>

        {account.loading ? (
          <div className="empty">正在读取账号状态…</div>
        ) : account.authenticated && user ? (
          <div className="stack">
            <div className="row">
              <span className="avatar" style={{ width: 42, height: 42, fontSize: 16 }}>
                {user.displayName.slice(0, 1).toUpperCase()}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600 }}>{user.displayName}</div>
                <div className="card-hint">{user.email}</div>
              </div>
              <span className="spacer" />
              <span className="badge" data-tone={user.status === 'active' ? 'ok' : 'muted'}>
                {user.status === 'active' ? '正常' : user.status === 'pending' ? '待激活' : '已停用'}
              </span>
            </div>

            {account.credits ? (
              <div className="credit-card">
                <div className="card-hint">积分合计（YoGo 全站共用）</div>
                <div className="credit-value">{account.credits.balance}</div>
                <div className="card-hint">
                  当日 {account.credits.dailyPoints}
                  {account.credits.dailyGrantQuota ? ` / ${account.credits.dailyGrantQuota}` : ''} · 永久{' '}
                  {account.credits.bonusPoints}
                </div>
              </div>
            ) : (
              <div className={`notice ${account.creditsError ? 'is-warn' : 'is-ok'}`} data-tone={account.creditsError ? 'warn' : 'ok'}>
                <span>{account.creditsError ? '⚠' : '✓'}</span>
                <span>{account.creditsError ?? '积分余额由账号中心统一记账。'}</span>
              </div>
            )}

            <div className="notice" data-tone="ok">
              <span>✓</span>
              <span>
                {usage
                  ? `本地转换 ${usage.pricing.localCost} 分/次、AI 识别 ${usage.pricing.aiCost} 分/次；未登录每天 ${usage.anonymous.limit} 次本地转换免费（今日剩余 ${usage.anonymous.remaining} 次）。只有选择 AI 通道时源文件页面才会离开设备。`
                  : '本地转换 1 分/次、AI 识别 2 分/次；未登录每天 5 次本地转换免费。只有选择 AI 通道时源文件页面才会离开设备。'}
              </span>
            </div>

            <div className="row">
              <button type="button" className="btn" onClick={() => void account.refresh()} disabled={account.loading}>
                刷新余额
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => void account.signOut()}
                disabled={account.loading}
              >
                退出登录
              </button>
            </div>
          </div>
        ) : (
          <div className="stack">
            <h3 style={{ fontSize: 18, fontWeight: 600 }}>登录 YoGo 账号</h3>
            <p className="card-hint">
              一个账号，YOGO 全系工具通用：登录、授权与积分结算都由 YoGo 账号中心统一提供，
              本站不保存你的密码。
            </p>

            <button type="button" className="btn btn-primary btn-block" onClick={() => account.signIn()}>
              使用 YoGo 账号登录
            </button>

            <p className="card-hint">
              跳转到 account.yogo.wang 完成登录或注册；授权后会自动回到本站。
              本地转换（图片、文档）无需登录即可使用。
            </p>

            {account.error ? (
              <div className="notice" data-tone="danger">
                <span>⛔</span>
                <span>{account.error}</span>
              </div>
            ) : null}
          </div>
        )}
      </aside>
    </div>
  );
}
